
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eye, DownloadCloud, RefreshCw, Clock, Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from '@/hooks/use-toast';
import ViewFeedbackDialog from './dialogs/ViewFeedbackDialog';
import type { FeedbackContext, FeedbackDbStatus } from '@/types/feedback';

// Define basic feedback structure without circular references
type BasicFeedback = {
  id: string;
  created_at: string;
  content?: string;
  rating?: number;
};

// Define the feedback record type
type FeedbackRecord = {
  id: string;
  context?: string;
  status: string;
  created_at: string;
  updated_at?: string;
  user_id?: string;
  candidate_email?: string;
  recruiter_email?: string;
  recruiter_company?: string;
  feedbacks?: BasicFeedback | null;
};

const FeedbacksTab = () => {
  const [feedbacks, setFeedbacks] = useState<FeedbackRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [contextFilter, setContextFilter] = useState<FeedbackContext | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<FeedbackDbStatus | 'all'>('all');
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackRecord | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  
  // Stats
  const [averageResponseTime, setAverageResponseTime] = useState<number | null>(null);

  useEffect(() => {
    fetchFeedbacks();
  }, [contextFilter, statusFilter]);

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      // This gets all feedback requests with their related feedback data if available
      let query = supabase
        .from('feedback_requests')
        .select(`
          *,
          feedbacks:feedback_id (*)
        `)
        .order('created_at', { ascending: false });
      
      // Apply filters if they are set
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      console.log('Fetched feedbacks:', data);
      setFeedbacks(data || []);
      
      // Calculate average response time for completed requests
      const completedRequests = data?.filter(req => req.status === 'completed' && req.feedbacks) || [];
      if (completedRequests.length > 0) {
        const totalResponseTime = completedRequests.reduce((sum, req) => {
          const requestDate = new Date(req.created_at);
          const responseDate = new Date(req.feedbacks?.created_at || req.created_at);
          return sum + (responseDate.getTime() - requestDate.getTime());
        }, 0);
        
        // Average in hours
        const avgTimeInHours = Math.round((totalResponseTime / completedRequests.length) / (1000 * 60 * 60));
        setAverageResponseTime(avgTimeInHours);
      } else {
        setAverageResponseTime(null);
      }
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les feedbacks.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch(status) {
      case 'completed':
        return <Badge className="bg-green-500">Répondu</Badge>;
      case 'pending':
        return <Badge className="bg-amber-500">En attente</Badge>;
      case 'expired':
        return <Badge className="bg-red-500">Expiré</Badge>;
      case 'cancelled':
        return <Badge className="bg-gray-500">Annulé</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const handleViewFeedback = (feedback: FeedbackRecord) => {
    setSelectedFeedback(feedback);
    setIsViewDialogOpen(true);
  };

  const handleExportCSV = () => {
    // Filter feedbacks based on current search and filters
    const filteredFeedbacks = getFeedbacksToDisplay();
    
    // Create CSV content
    const headers = ['ID', 'Contexte', 'Envoyeur', 'Destinataire', 'Statut', 'Créé le'];
    const csvContent = [
      headers.join(','),
      ...filteredFeedbacks.map(fb => [
        fb.id,
        fb.context || 'N/A',
        fb.user_id ? fb.candidate_email : 'N/A',
        fb.recruiter_email || 'N/A',
        fb.status,
        new Date(fb.created_at).toLocaleDateString('fr-FR')
      ].join(','))
    ].join('\n');
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `feedbacks-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFeedbacksToDisplay = () => {
    return feedbacks.filter(feedback => {
      // Apply search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = searchTerm === '' || 
        (feedback.candidate_email && feedback.candidate_email.toLowerCase().includes(searchLower)) ||
        (feedback.recruiter_email && feedback.recruiter_email.toLowerCase().includes(searchLower));
      
      return matchesSearch;
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle>Gestion des feedbacks</CardTitle>
          <CardDescription>Visualisez et gérez tous les feedbacks de la plateforme</CardDescription>
        </div>
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            onClick={fetchFeedbacks}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Rafraîchir
          </Button>
          <Button 
            variant="outline" 
            onClick={handleExportCSV}
            className="flex items-center gap-2"
          >
            <DownloadCloud className="h-4 w-4" />
            Exporter CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row justify-between mb-4 gap-4">
          <div className="flex items-center space-x-2 md:w-1/2">
            <Search className="h-4 w-4 text-gray-400" />
            <Input
              placeholder="Rechercher par email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Select 
              value={contextFilter} 
              onValueChange={(value) => setContextFilter(value as FeedbackContext | 'all')}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Contexte" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous contextes</SelectItem>
                <SelectItem value="recruitment">Recrutement</SelectItem>
                <SelectItem value="freelance">Freelance</SelectItem>
                <SelectItem value="360">Évaluation 360°</SelectItem>
              </SelectContent>
            </Select>
            <Select 
              value={statusFilter} 
              onValueChange={(value) => setStatusFilter(value as FeedbackDbStatus | 'all')}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="completed">Répondu</SelectItem>
                <SelectItem value="expired">Expiré</SelectItem>
                <SelectItem value="cancelled">Annulé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {averageResponseTime !== null && (
          <div className="bg-gray-50 p-3 rounded-md mb-4 flex items-center">
            <Clock className="h-5 w-5 text-gray-500 mr-2" />
            <span className="text-sm text-gray-600">
              Délai moyen de réponse : <strong>{averageResponseTime} heure{averageResponseTime !== 1 ? 's' : ''}</strong>
            </span>
          </div>
        )}
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandBlue"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Envoyeur</TableHead>
                  <TableHead>Destinataire</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Créé le</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getFeedbacksToDisplay().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      Aucun feedback trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  getFeedbacksToDisplay().map((feedback) => (
                    <TableRow key={feedback.id}>
                      <TableCell className="font-mono text-xs">{feedback.id.substring(0, 8)}...</TableCell>
                      <TableCell>{feedback.candidate_email || "N/A"}</TableCell>
                      <TableCell>{feedback.recruiter_email || "N/A"}</TableCell>
                      <TableCell>{getStatusBadge(feedback.status)}</TableCell>
                      <TableCell>{new Date(feedback.created_at).toLocaleDateString('fr-FR')}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewFeedback(feedback)}
                          className="flex items-center"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Voir
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
        
        {selectedFeedback && (
          <ViewFeedbackDialog
            isOpen={isViewDialogOpen}
            onClose={() => setIsViewDialogOpen(false)}
            feedback={selectedFeedback}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default FeedbacksTab;
